import { useEffect, useRef, useState } from 'react';
import { FormInputProps } from '@/@types/form.types';
import EListPopupPickerWithSearch from '@/components/everridge/EListPopupPickerWithSearch';
import { useOrderServicesLookupApi } from '@/features/orders/hooks/useOrderServicesLookupApi';

type Props<T extends {}> = {
	selectVariant?: 'single' | 'multi';
	showSearch?: boolean;
	icon?: React.FC<React.SVGProps<SVGSVGElement>>;
} & FormInputProps<T>;

const OrderServicesLookup = <T extends {}>(props: Props<T>) => {
	const [searchValue, setSearchValue] = useState('');
	const { placeholder = 'Select service', label = 'Services' } = props;

	const loaderRef = useRef<HTMLDivElement>(null);

	const {
		orderServicesLookupList, 
		fetchOrderServicesLookupListNextPage,
		hasOrderServicesLookupListNextPage,
		isFetchingOrderServicesLookupListNextPage,
	} = useOrderServicesLookupApi({ search: searchValue });

	useEffect(() => {
		if (
			!loaderRef.current ||
			!hasOrderServicesLookupListNextPage ||
			isFetchingOrderServicesLookupListNextPage
		)
			return;

		const scrollableContainer =
			loaderRef.current.closest('.overflow-y-scroll');

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					fetchOrderServicesLookupListNextPage();
				}
			},
			{
				root: scrollableContainer,
				rootMargin: '100px',
			},
		);

		const current = loaderRef.current;
		observer.observe(current);

		return () => observer.unobserve(current);
	}, [
		fetchOrderServicesLookupListNextPage,
		hasOrderServicesLookupListNextPage,
		isFetchingOrderServicesLookupListNextPage,
	]);

	return (
		<EListPopupPickerWithSearch
			{...props}
			label={label}
			placeholder={placeholder}
			list={orderServicesLookupList ?? []}
			searchValue={searchValue}
			setSearchValue={setSearchValue}
			ref={loaderRef}
		/>
	);
};

export default OrderServicesLookup;
